/**
 * The signed-in user's crystal: server-derived XP, client-derived level.
 * Loaded once and shared, like the session — the rail, the dashboard and
 * the profile all read from here, and attune() is the single writer.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { crystal as api } from './api.js';
import { levelFor, crystalHue } from './crystal.js';
import { useSession } from './session.jsx';

const Ctx = createContext(null);

export function CrystalProvider({ children }) {
  const { user } = useSession();
  const [state, setState] = useState(null);
  const [burst, setBurst] = useState(0);
  const [leveledTo, setLeveledTo] = useState(null);

  const refresh = useCallback(async () => {
    const s = await api.state();
    setState(s);
    return s;
  }, []);

  useEffect(() => { if (user) refresh().catch(console.error); }, [user, refresh]);

  const attune = useCallback(async () => {
    const before = state ? levelFor(state.xp).level : 1;
    const s = await api.attune();
    setState(s);
    setBurst((b) => b + 1);
    const after = levelFor(s.xp).level;
    if (after > before) setLeveledTo(after);
    return s;
  }, [state]);

  const value = useMemo(() => ({
    state, refresh, attune, burst, leveledTo, clearLevelUp: () => setLeveledTo(null),
    info: levelFor(state?.xp ?? 0),
    hue: crystalHue(user?.id),
    seed: user?.id ?? 'seed',
    loaded: state !== null,
  }), [state, refresh, attune, burst, leveledTo, user?.id]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCrystal() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCrystal must be used inside <CrystalProvider>');
  return ctx;
}
